import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Search } from 'lucide-react';
import {
    Fragment,
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { NavItem } from '@/data/menu';
import { useNavigationMenu } from '@/hooks/useNavigationMenu';
import { normalizePath, pathMatches as pathMatchesShared } from '@/lib/menuNavigation';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import { cn } from '../ui/utils';

type AppTopNavProps = {
    className?: string;
};

function pathMatches(href: string | undefined, path: string): boolean {
    return pathMatchesShared(href, path);
}

function itemIsActive(item: NavItem, path: string): boolean {
    if (pathMatches(item.href, path)) {
        return true;
    }
    return Boolean(item.children?.some((child) => pathMatches(child.href, path)));
}

function parseGapPx(el: HTMLElement): number {
    const style = getComputedStyle(el);
    const gap = style.columnGap || style.gap || '0';
    const value = Number.parseFloat(gap);
    return Number.isFinite(value) ? value : 0;
}

function computeVisibleCount(
    available: number,
    widths: number[],
    moreWidth: number,
    gap: number,
): number {
    const total = widths.length;
    if (total === 0 || available <= 0) {
        return 0;
    }

    let allWidth = 0;
    for (let i = 0; i < total; i++) {
        allWidth += widths[i] + (i > 0 ? gap : 0);
    }
    if (allWidth <= available) {
        return total;
    }

    const budgetForItems = available - moreWidth - gap;
    if (budgetForItems <= 0) {
        return 0;
    }

    let count = 0;
    let used = 0;
    for (let i = 0; i < total; i++) {
        const itemWidthWithGap = widths[i] + (count > 0 ? gap : 0);
        if (used + itemWidthWithGap <= budgetForItems) {
            used += itemWidthWithGap;
            count++;
        } else {
            break;
        }
    }

    return count;
}

function TopNavLeaf({
    item,
    active,
    measure = false,
}: {
    item: NavItem;
    active: boolean;
    measure?: boolean;
}) {
    const Icon = item.icon;
    return (
        <RouterLink
            to={item.href ?? '#'}
            tabIndex={measure ? -1 : undefined}
            className={cn(
                'nx-topnav-item',
                active && !measure && 'nx-topnav-item--active',
            )}
            aria-current={!measure && active ? 'page' : undefined}
        >
            <Icon size={15} strokeWidth={1.9} aria-hidden />
            <span>{item.label}</span>
        </RouterLink>
    );
}

function TopNavParent({
    item,
    active,
    path,
    measure = false,
}: {
    item: NavItem;
    active: boolean;
    path: string;
    measure?: boolean;
}) {
    const navigate = useNavigate();
    const Icon = item.icon;
    const trigger = (
        <button
            type="button"
            tabIndex={measure ? -1 : undefined}
            className={cn(
                'nx-topnav-item nx-topnav-item--parent',
                active && !measure && 'nx-topnav-item--active',
            )}
            aria-haspopup={measure ? undefined : 'menu'}
            aria-current={!measure && active ? 'true' : undefined}
        >
            <Icon size={15} strokeWidth={1.9} aria-hidden />
            <span>{item.label}</span>
            <ChevronDown
                className="nx-topnav-chevron"
                size={14}
                strokeWidth={2}
                aria-hidden
            />
        </button>
    );

    if (measure) {
        return trigger;
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                sideOffset={8}
                collisionPadding={12}
                className="nx-topnav-dropdown"
            >
                {item.children?.map((child) => {
                    const childActive = pathMatches(child.href, path);
                    return (
                        <DropdownMenuItem
                            key={child.id}
                            className={cn(
                                childActive && 'nx-topnav-dropdown-item--active',
                            )}
                            onSelect={() =>
                                navigate(child.href)
                            }
                        >
                            {child.label}
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function TopNavItem({
    item,
    path,
    measure = false,
}: {
    item: NavItem;
    path: string;
    measure?: boolean;
}) {
    const hasChildren = Boolean(item.children?.length);
    const active = itemIsActive(item, path);

    if (!hasChildren) {
        return <TopNavLeaf item={item} active={active} measure={measure} />;
    }

    return (
        <TopNavParent
            item={item}
            active={active}
            path={path}
            measure={measure}
        />
    );
}

function normalizeText(text: string): string {
    return text
        .toLocaleLowerCase('pt-BR')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

function matchesQuery(label: string, query: string): boolean {
    return normalizeText(label).includes(normalizeText(query));
}

/** Filtra overflow do “Mais”: grupos flat (label + filhos) + folhas. */
function filterOverflowItems(items: NavItem[], rawQuery: string): NavItem[] {
    const query = rawQuery.trim();
    if (!query) {
        return items;
    }

    const result: NavItem[] = [];
    for (const item of items) {
        const hasChildren = Boolean(item.children?.length);
        if (!hasChildren) {
            if (matchesQuery(item.label, query)) {
                result.push(item);
            }
            continue;
        }

        const groupMatch = matchesQuery(item.label, query);
        const children = groupMatch
            ? item.children ?? []
            : (item.children ?? []).filter((child) =>
                  matchesQuery(child.label, query),
              );

        if (children.length > 0) {
            result.push({ ...item, children });
        }
    }
    return result;
}

function MoreMenuItem({ item, path }: { item: NavItem; path: string }) {
    const navigate = useNavigate();
    const hasChildren = Boolean(item.children?.length);
    const active = itemIsActive(item, path);
    const Icon = item.icon;

    if (!hasChildren) {
        return (
            <DropdownMenuItem
                className={cn('nx-topnav-more-item', active && 'nx-topnav-dropdown-item--active')}
                onSelect={() => navigate(item.href ?? '#')}
            >
                <Icon size={14} className="nx-topnav-more-item-icon" aria-hidden />
                <span>{item.label}</span>
            </DropdownMenuItem>
        );
    }

    return (
        <div className="nx-topnav-more-group">
            <DropdownMenuLabel className="nx-topnav-more-label">
                <Icon size={13} className="nx-topnav-more-label-icon" aria-hidden />
                <span>{item.label}</span>
            </DropdownMenuLabel>
            <div className="nx-topnav-more-children">
                {item.children?.map((child) => {
                    const childActive = pathMatches(child.href, path);
                    return (
                        <DropdownMenuItem
                            key={child.id}
                            className={cn(
                                'nx-topnav-more-child',
                                childActive && 'nx-topnav-dropdown-item--active',
                            )}
                            onSelect={() =>
                                navigate(child.href)
                            }
                        >
                            {child.label}
                        </DropdownMenuItem>
                    );
                })}
            </div>
        </div>
    );
}

function MoreMenu({
    items,
    path,
    active,
}: {
    items: NavItem[];
    path: string;
    active: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const filterRef = useRef<HTMLInputElement>(null);

    const filteredItems = useMemo(
        () => filterOverflowItems(items, filter),
        [items, filter],
    );

    const handleOpenChange = useCallback((next: boolean) => {
        setOpen(next);
        if (!next) {
            setFilter('');
        }
    }, []);

    return (
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'nx-topnav-item nx-topnav-item--parent nx-topnav-more',
                        active && 'nx-topnav-item--active',
                    )}
                    aria-haspopup="menu"
                    aria-label="Mais itens do menu"
                >
                    <span>Mais</span>
                    <ChevronDown
                        className="nx-topnav-chevron"
                        size={14}
                        strokeWidth={2}
                        aria-hidden
                    />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                sideOffset={8}
                collisionPadding={12}
                className="nx-topnav-more-panel"
                onCloseAutoFocus={(event) => event.preventDefault()}
            >
                <div
                    className="nx-topnav-more-filter"
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <Search
                        className="nx-topnav-more-filter-icon"
                        size={14}
                        strokeWidth={2}
                        aria-hidden
                    />
                    <input
                        ref={filterRef}
                        type="search"
                        className="nx-topnav-more-filter-input"
                        placeholder="Filtrar…"
                        value={filter}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        aria-label="Filtrar itens do menu"
                        onChange={(event) => setFilter(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                                if (filter) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setFilter('');
                                    filterRef.current?.focus();
                                }
                            }
                        }}
                    />
                </div>
                <div className="nx-topnav-more-list" role="none">
                    {filteredItems.length === 0 ? (
                        <p className="nx-topnav-more-empty">
                            Nenhum item encontrado
                        </p>
                    ) : (
                        filteredItems.map((item, index) => (
                            <Fragment key={item.id}>
                                {index > 0 ? (
                                    <DropdownMenuSeparator className="nx-topnav-more-separator" />
                                ) : null}
                                <MoreMenuItem item={item} path={path} />
                            </Fragment>
                        ))
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/**
 * Navegação horizontal do shell (modo menu superior).
 * Itens que cabem na largura ficam na barra; o restante vai para “Mais”.
 */
export default function AppTopNav({ className }: AppTopNavProps) {
    const location = useLocation();
    const path = normalizePath(location.pathname);
    const items = useNavigationMenu();
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const moreMeasureRef = useRef<HTMLButtonElement>(null);
    const [visibleCount, setVisibleCount] = useState(items.length);

    const recalculate = useCallback(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        const moreEl = moreMeasureRef.current;
        if (!container || !measure || !moreEl) {
            return;
        }

        const itemEls = Array.from(
            measure.querySelectorAll<HTMLElement>('[data-nx-topnav-measure-item]'),
        );
        if (itemEls.length !== items.length) {
            setVisibleCount(items.length);
            return;
        }

        const widths = itemEls.map((el) => el.offsetWidth);
        const gap = parseGapPx(measure);
        const next = computeVisibleCount(
            container.clientWidth,
            widths,
            moreEl.offsetWidth,
            gap,
        );
        setVisibleCount(next);
    }, [items.length]);

    useLayoutEffect(() => {
        recalculate();

        const container = containerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(() => {
            recalculate();
        });
        observer.observe(container);
        document.fonts.ready.then(recalculate);
        document.fonts.addEventListener('loadingdone', recalculate);
        return () => {
            observer.disconnect();
            document.fonts.removeEventListener('loadingdone', recalculate);
        };
    }, [recalculate, items]);

    if (!items.length) {
        return null;
    }

    const visibleItems = items.slice(0, visibleCount);
    const overflowItems = items.slice(visibleCount);
    const moreActive = overflowItems.some((item) => itemIsActive(item, path));

    return (
        <nav
            className={cn('nx-topnav', className)}
            aria-label="Navegação principal"
        >
            <div
                ref={measureRef}
                className="nx-topnav-measure"
                aria-hidden="true"
                inert
            >
                {items.map((item) => (
                    <div key={item.id} data-nx-topnav-measure-item="">
                        <TopNavItem item={item} path={path} measure />
                    </div>
                ))}
                <div data-nx-topnav-measure-more="">
                    <button
                        ref={moreMeasureRef}
                        type="button"
                        className="nx-topnav-item nx-topnav-item--parent"
                        tabIndex={-1}
                    >
                        <span>Mais</span>
                        <ChevronDown
                            className="nx-topnav-chevron"
                            size={14}
                            strokeWidth={2}
                            aria-hidden
                        />
                    </button>
                </div>
            </div>

            <div ref={containerRef} className="nx-topnav-bar">
                {visibleItems.map((item) => (
                    <TopNavItem key={item.id} item={item} path={path} />
                ))}

                {overflowItems.length > 0 ? (
                    <MoreMenu
                        items={overflowItems}
                        path={path}
                        active={moreActive}
                    />
                ) : null}
            </div>
        </nav>
    );
}
